import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import type { Unternehmen } from "@kundeninfo/api-client";
import { darfAbbrechen } from "@kundeninfo/ui";
import { Verweis } from "@kundeninfo/ui/me-design";
import { istMeDialog, routeToPath } from "../../router.js";
import type { Route } from "../../router.js";
import { SUPPLIER_TABS } from "../../views/aktenReiter.js";
import type { TicketsClient } from "../../views/tickets/ticketsClient.js";
import type { MilestonesClient } from "../../views/milestones/milestonesClient.js";
import type { SupplierContractsClient } from "../../views/suppliers/KundeLiefervertraege.js";
import type { IncomingListClient } from "../../views/incomingInvoices/KundeEingangsrechnungen.js";
import { gleicherBezug, istGastBezug, istSprungBezug, sprungZiel } from "./objektBezug.js";
import type { GastBezug, ObjektBezug, PanelArt } from "./objektBezug.js";
import { OBJEKT_TEXT } from "./objektTexte.js";
import { ObjektPanelKontext } from "./ObjektPanelKontext.js";
import { TicketInhalt } from "./inhalte/TicketInhalt.js";
import { MeilensteinInhalt } from "./inhalte/MeilensteinInhalt.js";
import { LiefervertragInhalt } from "./inhalte/LiefervertragInhalt.js";
import { EingangsrechnungInhalt } from "./inhalte/EingangsrechnungInhalt.js";
import type { ObjektPanelWege, OeffnenOptionen } from "./ObjektPanelKontext.js";

/** Der Klient aller Inhalte — die EINE Stelle, die die Aufgaben 2/3 ergänzen (PF20). */
export type ObjektPanelClient = TicketsClient & MilestonesClient & SupplierContractsClient & IncomingListClient;

/** Was der Benutzer darf (F12): Lieferantensicht, eigene Kennung für Tickets, voller Zuschnitt. */
export type GastgeberRechte = { supplierView: boolean; ich: { id: string; name: string; istAdmin: boolean } | null; zuschnittVoll: boolean };

/**
 * Was ein Inhalt bekommt. Er zeichnet sein Panel selbst (`ObjektArbeitsdialog` bzw. `TicketDialog` mit `verlauf`) und
 * meldet: `onUngespeichert` (Rückfrage beim Ersetzen und beim Ausgang), `onGeaendert` nach jedem Schreibweg (F9),
 * `onSchliessen` ohne Argument, nachdem er selbst gefragt hat. Navigieren geht nur über `onSpringe`/`onVerlasse` (PF3).
 */
export type InhaltProps<A extends PanelArt = PanelArt> = {
  bezug: GastBezug & { art: A }; seitlich: boolean; client: ObjektPanelClient; companies: readonly Unternehmen[];
  /** Das Arbeitsunternehmen der Sitzung (dieselbe Quelle wie die Kopfzeile): Ein Objekt eines anderen Unternehmens nennt
   *  dessen Kürzel als Kennzeichen (Spec F12, `kennzeichenFolge`). `null` ohne Arbeitsunternehmen. */
  sitzungsUnternehmen: string | null;
  rechte: GastgeberRechte; sprung: ReactNode;
  /** Der Kunde der Akte unter dem Panel (`null` außerhalb einer Akte) — der Lieferant steht nur außerhalb seiner Akte im
   *  Bezug (P9). */
  akteKunde: string | null;
  onUngespeichert(ja: boolean): void; onGeaendert(): void; onSchliessen(): void;
  onWechsel(b: ObjektBezug): void; onSpringe(ziel: ObjektBezug): void; onVerlasse(ziel: Route): void;
};

export type ObjektInhalte = { readonly [A in PanelArt]?: (p: InhaltProps<A>) => ReactElement };

/** Die Inhalte je Art (Spec F6): Ticket und Meilenstein (Aufgabe 2), Liefervertrag und Eingangsrechnung (Aufgabe 3). */
export const INHALTE: ObjektInhalte = { ticket: TicketInhalt, meilenstein: MeilensteinInhalt, liefervertrag: LiefervertragInhalt,
  eingangsrechnung: EingangsrechnungInhalt };

/** Rückfall, wenn das popstate des Verlaufseintrags ausbleibt (P7). */
const RUECKFALL_MS = 500;

type Ebene = { schluessel: number; bezug: GastBezug; seitlich: boolean; onGeaendert: (() => void) | undefined;
  /** Der Dialog der Stelle unter dieser Ebene (`OeffnenOptionen.darunter`) — reist beim Ersetzen mit. */
  darunter: ObjektBezug | undefined;
  /** Was der Inhalt meldet — ein veränderlicher Kasten, gelesen nur von Rückfrage, Ausgang und Schließen, nie beim Zeichnen. */
  stand: { geaendert: boolean; ungespeichert: boolean } };

/** Die Rechte als Werte (stabile Abhängigkeiten der Wege). */
type Recht = { supplierView: boolean; ichDa: boolean; zuschnittVoll: boolean };

/** Darf der Benutzer an das Objekt — öffnen oder springen (F12)? Der Server bleibt der Riegel. */
function darf(b: ObjektBezug, r: Recht): boolean {
  switch (b.art) {
    case "ticket": case "meilenstein": return r.ichDa && r.zuschnittVoll;
    case "liefervertrag": case "eingangsrechnung": return r.supplierView;
    case "angebot": case "auftrag": case "rechnung": return true;
    case "akte": return r.supplierView || !SUPPLIER_TABS.includes(b.reiter);
    case "konto": case "planposition": return false;
  }
}

/** Wartet auf das nächste popstate (einmaliger Hörer) oder die Frist; `true`, wenn es kam. */
function popstateOderFrist(ms: number): Promise<boolean> {
  return new Promise((fertig) => {
    const ende = (kam: boolean): void => {
      window.removeEventListener("popstate", hoerer);
      window.clearTimeout(uhr);
      fertig(kam);
    };
    const hoerer = (): void => ende(true);
    const uhr = window.setTimeout(() => ende(false), ms);
    window.addEventListener("popstate", hoerer);
  });
}

/**
 * DER GASTGEBER DER OBJEKTPANELS (Spec F4–F13, P6, P7; PF1 Weg A, PF3) — eine Instanz in App.tsx um den Rahmen.
 *
 * Höchstens zwei Ebenen: Hauptpanel und seitliches. Seitlich öffnet, was aus einem Dialog heraus kommt (`useObjektPanel`
 * setzt das aus `useImModal`) oder über einer offenen Hauptebene; ein weiteres Objekt ersetzt das seitliche nach der
 * Rückfrage; liegt es schon darunter, schließt das seitliche (F14). Jede Ebene trägt `verlauf` (Entscheid 696, je Ebene
 * ein Eintrag; me-design hält die Einträge je Fenster, W-5). Den Fokus gibt me-dialog nach dem Schließen an den Knopf im
 * Schatten zurück, der das Panel geöffnet hat (W-8, PF15). Ein Wechsel der Adresse leert die Ebenen ohne
 * `onGeaendert`. Jede Navigation aus einer Ebene geht durch `verlasse` — nie am Panel vorbei, sonst nähme sein
 * `history.go(-1)` die eben gemachte Navigation zurück.
 */
export function ObjektPanelGastgeber(p: { route: Route; navigiere: (r: Route, ersetzen?: boolean) => void; client: ObjektPanelClient;
  companies: readonly Unternehmen[]; sitzungsUnternehmen: string | null; rechte: GastgeberRechte; inhalte?: ObjektInhalte;
  children: ReactNode }): ReactElement {
  const inhalte = p.inhalte ?? INHALTE;
  const [ebenen, setEbenen] = useState<readonly Ebene[]>([]);
  /** Der Stand für die asynchronen Wege (Rückfrage, Ausgang) — immer zugleich mit `ebenen` gesetzt (`setze`). */
  const aktuell = useRef<readonly Ebene[]>([]);
  const setze = (neu: readonly Ebene[]): void => { aktuell.current = neu; setEbenen(neu); };
  /** Läuft eine Rückfrage oder ein Ausgang, nimmt der Gastgeber kein weiteres Öffnen an. */
  const beschaeftigt = useRef(false);
  const zaehler = useRef(0);
  const ort = useRef({ route: p.route, navigiere: p.navigiere });
  ort.current = { route: p.route, navigiere: p.navigiere };

  const { supplierView, zuschnittVoll } = p.rechte;
  const ichDa = p.rechte.ich !== null;

  const { wege, intern } = useMemo(() => {
    const rechte: Recht = { supplierView, zuschnittVoll, ichDa };
    const kannOeffnen = (b: ObjektBezug): boolean => istGastBezug(b) && inhalte[b.art] !== undefined && darf(b, rechte);
    // Ein Sprung, der die Adresse nicht wechselt, schlösse nur die Panels: `navigiere` bleibt bei gleichem Pfad stehen,
    // ein anderer Vorgang auf derselben Adresse käme nie an (wie P9: kein Sprung im eigenen Reiter).
    const kannSpringen = (b: ObjektBezug): boolean => istSprungBezug(b) && darf(b, rechte)
      && routeToPath(sprungZiel(b, ort.current.route)) !== routeToPath(ort.current.route);

    const neueEbene = (bezug: GastBezug, seitlich: boolean, onGeaendert: (() => void) | undefined,
      darunter: ObjektBezug | undefined): Ebene =>
      ({ schluessel: ++zaehler.current, bezug, seitlich, onGeaendert, darunter, stand: { geaendert: false, ungespeichert: false } });
    /**
     * Nimmt die Ebenen ab `ab` heraus (den Fokus gibt me-dialog an den Auslöser zurück, W-8) und meldet `onGeaendert` an
     * die Stellen, deren Panel geschrieben hat (F9) — auch im Ausgang: Hält der Verlassen-Wächter der Ansicht die Navigation
     * an, stünde sie sonst veraltet da. Nur ein Wechsel der Adresse von außen (Effekt unten) räumt ohne `onGeaendert` ab (P6).
     */
    const entferne = (ab: number): void => {
      const weg = aktuell.current.slice(ab);
      if (weg.length === 0) return;
      setze(aktuell.current.slice(0, ab));
      for (const e of [...weg].reverse()) if (e.stand.geaendert) e.onGeaendert?.();
    };
    /** Rückfrage über alle Ebenen ab `ab`, von oben (Hausregel: gestapelt über dem Panel). */
    const duerfenZu = async (ab: number): Promise<boolean> => {
      for (const e of aktuell.current.slice(ab).reverse()) if (!(await darfAbbrechen(e.stand.ungespeichert))) return false;
      return true;
    };
    const exklusiv = async (lauf: () => Promise<void>): Promise<void> => {
      if (beschaeftigt.current) return;
      beschaeftigt.current = true;
      try { await lauf(); } finally { beschaeftigt.current = false; }
    };
    /** Eine Ebene durch ein anderes Objekt ersetzen — in einem Zug, damit me-dialog nur den Besitzer des Eintrags tauscht. */
    const ersetze = (alt: Ebene, b: GastBezug, onGeaendert: (() => void) | undefined): Promise<void> => exklusiv(async () => {
      if (!(await darfAbbrechen(alt.stand.ungespeichert))) return;
      const stelle = aktuell.current.indexOf(alt);
      if (stelle < 0) return;
      if (alt.stand.geaendert) alt.onGeaendert?.();
      setze([...aktuell.current.slice(0, stelle), neueEbene(b, alt.seitlich, onGeaendert, alt.darunter),
        ...aktuell.current.slice(stelle + 1)]);
    });

    /**
     * F14: Liegt das Objekt schon in einer Ebene, schließen die darüber (Rückfrage) — nie eine zweite Fassung davon. Ebenso,
     * wenn es der Dialog der Stelle unter einer Ebene ist (`darunter`): dann schließt diese Ebene mit.
     */
    const schonOffen = (b: GastBezug): boolean => {
      const offen = aktuell.current.findIndex((e) => gleicherBezug(e.bezug, b));
      const unter = aktuell.current.findIndex((e) => e.darunter !== undefined && gleicherBezug(e.darunter, b));
      const ab = offen >= 0 ? offen + 1 : unter;
      if (ab >= 0 && ab < aktuell.current.length) void exklusiv(async () => { if (await duerfenZu(ab)) entferne(ab); });
      return ab >= 0;
    };

    const oeffne = (b: ObjektBezug, o: OeffnenOptionen = {}): void => {
      if (beschaeftigt.current || !istGastBezug(b) || !kannOeffnen(b) || schonOffen(b)) return;
      const liste = aktuell.current;
      const oben = liste.at(-1);
      if (oben?.seitlich === true) { void ersetze(oben, b, o.onGeaendert); return; }
      setze([...liste, neueEbene(b, o.seitlich === true || oben !== undefined, o.onGeaendert, o.darunter)]);
    };

    // Ohne offene Ebene läuft die Schleife nicht, und `exklusiv` navigiert noch im selben Aufruf.
    const verlasse = (ziel: Route): void => {
      void exklusiv(async () => {
        let rueckfall = false;
        for (let oben = aktuell.current.at(-1); oben !== undefined; oben = aktuell.current.at(-1)) {
          if (!(await darfAbbrechen(oben.stand.ungespeichert))) return;
          const echo = istMeDialog(window.history.state) ? popstateOderFrist(RUECKFALL_MS) : Promise.resolve(true);
          entferne(aktuell.current.length - 1);
          if (!(await echo)) rueckfall = true;
        }
        // Blieb ein Echo aus, steht der Zeiger womöglich noch auf einem toten Panel-Eintrag: den ersetzen, statt darüber
        // einen neuen zu legen, auf den die Zurück-Taste vom Ziel aus fiele (PF3).
        if (rueckfall && istMeDialog(window.history.state)) ort.current.navigiere(ziel, true);
        else ort.current.navigiere(ziel);
      });
    };

    const schliesse = (schluessel: number): void => {
      const stelle = aktuell.current.findIndex((e) => e.schluessel === schluessel);
      if (stelle >= 0) entferne(stelle);
    };
    const wechsle = (schluessel: number, b: ObjektBezug): void => {
      const alt = aktuell.current.find((e) => e.schluessel === schluessel);
      if (alt !== undefined && istGastBezug(b) && kannOeffnen(b) && !schonOffen(b)) void ersetze(alt, b, alt.onGeaendert);
    };
    const springe = (b: ObjektBezug): void => { if (istSprungBezug(b) && kannSpringen(b)) verlasse(sprungZiel(b, ort.current.route)); };
    const wege: ObjektPanelWege = { oeffne, springe, verlasse, kannOeffnen, kannSpringen };
    return { wege, intern: { schliesse, wechsle } };
  }, [supplierView, zuschnittVoll, ichDa, inhalte]);

  // Ein Wechsel der Adresse schließt alle Ebenen, ohne `onGeaendert` (P6): die Ansicht darunter ist ohnehin eine andere.
  const pfad = routeToPath(p.route);
  const letzterPfad = useRef(pfad);
  useEffect(() => {
    if (letzterPfad.current === pfad) return;
    letzterPfad.current = pfad;
    if (aktuell.current.length > 0) setze([]);
  }, [pfad]);

  return (
    <ObjektPanelKontext.Provider value={wege}>
      {p.children}
      {ebenen.map((e) => {
        const Inhalt = inhalte[e.bezug.art] as ((q: InhaltProps) => ReactElement) | undefined;
        if (Inhalt === undefined) return null;
        const b = e.bezug;
        const sprung = istSprungBezug(b) && wege.kannSpringen(b)
          ? <Verweis sprung={OBJEKT_TEXT.sprung(b)} onSprung={() => wege.springe(b)} /> : null;
        return (
          <Inhalt key={e.schluessel} bezug={b} seitlich={e.seitlich} client={p.client} companies={p.companies}
            sitzungsUnternehmen={p.sitzungsUnternehmen} rechte={p.rechte}
            sprung={sprung} akteKunde={p.route.name === "kunde" ? p.route.id : null} onUngespeichert={(ja) => { e.stand.ungespeichert = ja; }}
            onGeaendert={() => { e.stand.geaendert = true; }} onSchliessen={() => intern.schliesse(e.schluessel)}
            onWechsel={(ziel) => intern.wechsle(e.schluessel, ziel)} onSpringe={wege.springe} onVerlasse={wege.verlasse} />
        );
      })}
    </ObjektPanelKontext.Provider>
  );
}
